import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../app/store';
import fetchPrebuiltTemplates from '../../features/templates/prebuiltTemplatesApi';
import fetchUserDefinedTemplates from '../../features/templates/userDefinedTemplatesApi';
import {
  PrebuiltTemplate,
  UserDefinedTemplate,
} from '../../features/templates/types';
import { BaseTemplate } from '../../features/templates/templatesSlice';
import YourTemplates from './YourTemplates';
import TemplatesGallery from './TemplatesGallery';
import UnderReview from './UnderReview';
import Rejected from './Rejected';
import NewTemplates from './NewTemplates';
import ApprovedTemplates from './ApprovedTemplates';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';

const Templates: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const tabParam = searchParams.get('tab');

  // === DEMO ROLE STATE ===
  const [role, setRole] = useState<'user' | 'admin'>(() => {
    const saved = localStorage.getItem('demoRole');
    return (saved as 'user' | 'admin') || 'user';
  });

  // const handleRoleChange = (newRole: 'user' | 'admin') => {
  //   setRole(newRole);
  //   localStorage.setItem('demoRole', newRole);
  //   const defaultTab = newRole === 'admin' ? 'newTemplates' : 'yourTemplates';
  //   setActiveTab(defaultTab);
  //   setSearchParams({ tab: defaultTab });
  // };
  // =======================

  const getLastActiveTab = (roleType: 'user' | 'admin'): string => {
    const storageKey = `lastActiveTab_${roleType}`;
    const savedTab = localStorage.getItem(storageKey);

    const validUserTabs = [
      'yourTemplates',
      'templatesGallery',
      'underReview',
      'rejected',
    ];
    const validAdminTabs = ['newTemplates', 'approvedTemplates', 'rejected'];

    if (tabParam) {
      if (roleType === 'admin' && validAdminTabs.includes(tabParam)) {
        return tabParam;
      }
      if (validUserTabs.includes(tabParam)) {
        return tabParam;
      }
    }

    if (savedTab) {
      if (roleType === 'admin' && validAdminTabs.includes(savedTab)) {
        return savedTab;
      }
      if (validUserTabs.includes(savedTab)) {
        return savedTab;
      }
    }

    return roleType === 'admin' ? 'newTemplates' : 'yourTemplates';
  };

  const [activeTab, setActiveTab] = useState<
    | 'yourTemplates'
    | 'templatesGallery'
    | 'underReview'
    | 'rejected'
    | 'newTemplates'
    | 'approvedTemplates'
  >(() => {
    const defaultTab = getLastActiveTab(role);
    return defaultTab as any;
  });

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const prebuiltStatus = useSelector(
    (state: RootState) => state.templates.prebuiltStatus
  );
  const userDefinedStatus = useSelector(
    (state: RootState) => state.templates.userDefinedStatus
  );
  // const prebuiltError = useSelector(
  //   (state: RootState) => state.templates.prebuiltError
  // );
  // const userDefinedError = useSelector(
  //   (state: RootState) => state.templates.userDefinedError
  // );

  const userTemplates = useSelector(
    (state: RootState) => state.templates.userTemplates
  );
  const userDefinedTemplates = useSelector(
    (state: RootState) => state.templates.userDefinedTemplates
  );
  const prebuiltTemplates = useSelector(
    (state: RootState) =>
      state.templates.prebuiltTemplates as PrebuiltTemplate[]
  );
  const userDefinedSearchTerm = useSelector(
    (state: RootState) => state.templates.userDefinedSearchTerm
  );
  const prebuiltSearchTerm = useSelector(
    (state: RootState) => state.templates.prebuiltSearchTerm
  );

  // Get pagination state from Redux
  const userDefinedPagination = useSelector(
    (state: RootState) => state.templates.userDefinedPagination
  );
  const prebuiltPagination = useSelector(
    (state: RootState) => state.templates.prebuiltPagination
  );

  const { currentPage, totalPages, totalCount, pageSize } = userDefinedPagination;
  const prebuiltCurrentPage = prebuiltPagination?.currentPage || 0;
  const prebuiltTotalPages = prebuiltPagination?.totalPages || 0;
  const prebuiltTotalCount = prebuiltPagination?.totalCount || 0;
  const prebuiltPageSize = prebuiltPagination?.pageSize || 9;

  // Function to fetch templates with pagination (no search)
  const fetchTemplates = (page: number = 0) => {
    dispatch(fetchUserDefinedTemplates({
      orgId: 'org_123456',
      page: page,
      size: 9,
    }));
  };

  // Function to fetch templates with search (with page=0 and size=9)
  const searchTemplates = (searchTerm: string) => {
    dispatch(fetchUserDefinedTemplates({
      orgId: 'org_123456',
      page: 0,
      size: 9,
      name: searchTerm,
    }));
  };

  // Function to fetch prebuilt templates with pagination
  const fetchPrebuilt = (page: number = 0, searchTerm: string = '') => {
    dispatch(fetchPrebuiltTemplates({
      orgId: 'org_123456',
      page: page,
      size: 9,
      name: searchTerm,
    }));
  };

  // Function to fetch prebuilt templates with search (with page=0 and size=9)
  const searchPrebuilt = (searchTerm: string) => {
    dispatch(fetchPrebuiltTemplates({
      orgId: 'org_123456',
      page: 0,
      size: 9,
      name: searchTerm,
    }));
  };

  // Handle page change for user-defined templates
  const handlePageChange = (page: number) => {
    fetchTemplates(page);
  };

  // Handle page change for prebuilt templates
  const handlePrebuiltPageChange = (page: number) => {
    fetchPrebuilt(page, prebuiltSearchTerm);
  };

  // Initial fetch
  useEffect(() => {
    if (prebuiltStatus === 'idle') {
      fetchPrebuilt(0, '');
    }
    if (userDefinedStatus === 'idle') {
      fetchTemplates(0);
    }
  }, [prebuiltStatus, userDefinedStatus, dispatch]);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    if (activeTab) {
      const storageKey = `lastActiveTab_${role}`;
      localStorage.setItem(storageKey, activeTab);
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, role, setSearchParams]);

  useEffect(() => {
    if (location.state?.refresh !== true) return;

    if (activeTab === 'templatesGallery' && prebuiltStatus !== 'loading') {
      fetchPrebuilt(0, '');
    }

    if ((activeTab === 'yourTemplates' || activeTab === 'underReview' ||
      activeTab === 'rejected' || activeTab === 'newTemplates' ||
      activeTab === 'approvedTemplates') && userDefinedStatus !== 'loading') {
      fetchTemplates(currentPage);
    }

    navigate(location.pathname + location.search, {
      replace: true,
      state: {},
    });
  }, [location.state?.refresh, activeTab, prebuiltStatus, userDefinedStatus,
    dispatch, navigate, location.pathname, location.search, currentPage]);

  useEffect(() => {
    if (tabParam) {
      const validUserTabs = [
        'yourTemplates',
        'templatesGallery',
        'underReview',
        'rejected',
      ];
      const validAdminTabs = ['newTemplates', 'approvedTemplates', 'rejected'];

      const isValidTab = role === 'admin'
        ? validAdminTabs.includes(tabParam)
        : validUserTabs.includes(tabParam);

      if (isValidTab && activeTab !== tabParam) {
        setActiveTab(tabParam as any);
      }
    }
  }, [tabParam, role, activeTab]);

  const handleTabChange = (
    tab:
      | 'yourTemplates'
      | 'templatesGallery'
      | 'underReview'
      | 'rejected'
      | 'newTemplates'
      | 'approvedTemplates'
  ) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Filter templates by status
  const draftTemplates = userDefinedTemplates.filter(
    (t) => t.status === 'DRAFT'
  );
  const approvedTemplates = userDefinedTemplates.filter(
    (t) => t.status === 'APPROVED'
  );
  const underReviewTemplates = userDefinedTemplates.filter(
    (t) => t.status === 'UNDER_REVIEW'
  );
  const rejectedTemplates = userDefinedTemplates.filter(
    (t) => t.status === 'REJECTED'
  );

  const yourTemplatesList = [...draftTemplates, ...approvedTemplates];

  const allGalleryTemplates: (
    | BaseTemplate
    | PrebuiltTemplate
    | UserDefinedTemplate
  )[] = [
      ...prebuiltTemplates,
      ...userDefinedTemplates,
      ...userTemplates.filter((t) => !t.isPrebuilt && !t.isUserDefined),
    ];

  const handleSelect = (id: string) => navigate(`/templates/${id}`);
  const handleEdit = (id: string) => navigate(`/templates/${id}`);

  return (
    <div className='p-2 bg-gray-50 min-h-screen'>
      <div className='flex justify-between items-center mb-4'>
        <div>
          <h2 className='text-2xl font-bold text-gray-800'>Templates</h2>
          <p className='text-gray-600'>
            Create and manage your email campaigns to reach the right audience
            at the right time.
          </p>
        </div>
      </div>

      <div className='bg-white rounded-lg shadow-sm border'>
        <div className='flex space-x-8 px-6 pt-4 border-b overflow-x-auto'>
          {role === 'user' ? (
            <>
              <button
                onClick={() => handleTabChange('yourTemplates')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'yourTemplates'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Templates
              </button>
              <button
                onClick={() => handleTabChange('templatesGallery')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'templatesGallery'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Prebuilt Templates
              </button>
              <button
                onClick={() => handleTabChange('underReview')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'underReview'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Under Review
              </button>
              <button
                onClick={() => handleTabChange('rejected')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'rejected'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Rejected
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => handleTabChange('newTemplates')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'newTemplates'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                New Templates
              </button>
              <button
                onClick={() => handleTabChange('approvedTemplates')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'approvedTemplates'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Approved Templates
              </button>
              <button
                onClick={() => handleTabChange('rejected')}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'rejected'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
              >
                Rejected
              </button>
            </>
          )}
        </div>

        <div className='p-6'>
          {/* USER VIEW */}
          {role === 'user' && activeTab === 'yourTemplates' && (
            <YourTemplates
              fromTab2={activeTab}
              templates={yourTemplatesList}
              onSelect={handleSelect}
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onSearch={(searchTerm) => searchTemplates(searchTerm)}
              searchTerm={userDefinedSearchTerm}
            />
          )}

          {role === 'user' && activeTab === 'templatesGallery' && (
            <TemplatesGallery
              fromTab2={activeTab}
              templates={allGalleryTemplates}
              onSelect={handleSelect}
              onEdit={handleEdit}
              onSearch={(searchTerm) => searchPrebuilt(searchTerm)}
              searchTerm={prebuiltSearchTerm}
              currentPage={prebuiltCurrentPage}
              totalPages={prebuiltTotalPages}
              totalCount={prebuiltTotalCount}
              pageSize={prebuiltPageSize}
              onPageChange={handlePrebuiltPageChange}
            />
          )}

          {role === 'user' && activeTab === 'underReview' && (
            <>
              {userDefinedStatus === 'loading' && (
                <p className='text-center text-gray-500 py-12'>
                  Loading templates under review...
                </p>
              )}
              {userDefinedStatus === 'succeeded' && (
                <UnderReview
                  templates={underReviewTemplates}
                  onSelect={handleSelect}
                />
              )}
            </>
          )}

          {/* ADMIN VIEW */}
          {role === 'admin' && activeTab === 'newTemplates' && (
            <NewTemplates
              templates={draftTemplates}
              onSelect={handleSelect}
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
            />
          )}

          {role === 'admin' && activeTab === 'approvedTemplates' && (
            <ApprovedTemplates
              templates={approvedTemplates}
              onSelect={handleSelect}
            />
          )}

          {/* REJECTED TAB – shared for both roles */}
          {activeTab === 'rejected' && (
            <>
              {userDefinedStatus === 'loading' && (
                <p className='text-center text-gray-500 py-12'>
                  Loading rejected templates...
                </p>
              )}
              {userDefinedStatus === 'succeeded' && (
                <Rejected
                  role={role}
                  templates={rejectedTemplates}
                  onSelect={handleSelect}
                />
              )}
            </>
          )}
        </div>
      </div>

      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default Templates;